import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { toast } from '../hooks/use-toast';
import { Car, Phone, Lock, MessageSquare, RefreshCw } from 'lucide-react';
import { BrandLogo } from './BrandLogo';

const LoginPage = () => {
  const navigate = useNavigate();
  const { sendUserOtp, verifyUserOtp, formatApiError } = useAuth();
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [devOtp, setDevOtp] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((r) => r - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const handleSendOtp = async () => {
    if (!phone || phone.length < 10) {
      toast({
        title: 'Invalid Phone',
        description: 'Please enter a valid 10-digit phone number',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      const data = await sendUserOtp(phone);
      setShowOtpInput(true);
      setOtp('');
      setDevOtp(data.dev_otp || '');
      setMaskedPhone(data.masked_phone || '');
      setResendIn(data.resend_after_seconds ?? 30);
      toast({
        title: 'OTP Sent!',
        description: data.dev_otp
          ? `Test OTP: ${data.dev_otp}`
          : `A 6-digit code was sent via SMS to ${data.masked_phone || 'your phone'}`,
        duration: 10000,
      });
    } catch (err) {
      toast({ title: 'Could not send OTP', description: formatApiError(err), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePhone = () => {
    setShowOtpInput(false);
    setOtp('');
    setDevOtp('');
    setMaskedPhone('');
    setResendIn(0);
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      toast({ title: 'Invalid OTP', description: 'OTP must be 6 digits', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await verifyUserOtp(phone, otp);
      toast({ title: 'Login Successful!', description: 'Welcome!' });
      navigate('/user/dashboard');
    } catch (err) {
      toast({ title: 'Login Failed', description: formatApiError(err), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-card to-muted flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <BrandLogo variant="compact" className="h-14 w-auto mx-auto mb-4" />
          <p className="text-muted-foreground">Book drivers by the hour</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>User Login</CardTitle>
            <CardDescription>Login with your phone number to book drivers</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone-user">Phone Number</Label>
              <div className="flex gap-2">
                <Input
                  id="phone-user"
                  type="tel"
                  placeholder="Enter 10-digit number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={showOtpInput}
                  className="flex-1"
                  maxLength={10}
                  data-testid="login-phone-input"
                />
                {!showOtpInput && (
                  <Button onClick={handleSendOtp} disabled={submitting} data-testid="send-otp-btn">
                    <Phone className="w-4 h-4 mr-2" />
                    Send OTP
                  </Button>
                )}
              </div>
            </div>

            {showOtpInput && (
              <div className="space-y-2">
                <Label htmlFor="otp-user">Enter OTP</Label>
                <div className="flex gap-2">
                  <Input
                    id="otp-user"
                    type="text"
                    placeholder="Enter 6-digit OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    maxLength={6}
                    className="flex-1"
                    data-testid="login-otp-input"
                  />
                  <Button onClick={handleVerifyOtp} disabled={submitting} data-testid="verify-otp-btn">
                    <Lock className="w-4 h-4 mr-2" />
                    Verify
                  </Button>
                </div>
                {devOtp ? (
                  <p className="text-sm text-muted-foreground" data-testid="dev-otp-display">
                    Test OTP: <span className="font-mono font-bold">{devOtp}</span>
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground flex items-center gap-2" data-testid="otp-sent-to">
                    <MessageSquare className="w-4 h-4 text-green-600" />
                    We sent a 6-digit code via SMS to <span className="font-semibold">{maskedPhone}</span>
                  </p>
                )}
                <div className="flex items-center justify-between pt-1">
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 h-auto text-xs"
                    onClick={handleChangePhone}
                    data-testid="change-phone-btn"
                  >
                    Change number
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 h-auto text-xs"
                    onClick={handleSendOtp}
                    disabled={submitting || resendIn > 0}
                    data-testid="resend-otp-btn"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend OTP'}
                  </Button>
                </div>
              </div>
            )}

            {/* Driver Login Link */}
            <div className="mt-6 pt-6 border-t">
              <p className="text-sm text-muted-foreground text-center mb-3">Are you a driver?</p>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/driver/login')}
              >
                <Car className="w-4 h-4 mr-2" />
                Driver Login
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-2">
                New driver?{' '}
                <Button
                  variant="link"
                  className="text-xs p-0 h-auto"
                  onClick={() => navigate('/driver/register')}
                >
                  Register here
                </Button>
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <Button variant="ghost" onClick={() => navigate('/')}>
            ← Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
